import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { calculateHaversineDistance, extractCoordinates, getCurrentPosition } from '../utility/geo.utils';
import { AppSettingsService } from './app-settings.service';

export interface UserCoordinates {
  latitude: number;
  longitude: number;
}

@Injectable({ providedIn: 'root' })
export class GeoService {
  private settings = inject(AppSettingsService);
  private translate = inject(TranslateService);

  public userCoordinates = signal<UserCoordinates | null>(null);
  public permissionState = signal<PermissionState | 'unsupported' | 'unknown'>('unknown');
  public isRequesting = signal<boolean>(false);
  public lastError = signal<string | null>(null);

  constructor() {
    this.checkPermissionState();
  }

  /**
   * Queries navigator.permissions for geolocation if supported by browser.
   */
  public async checkPermissionState(): Promise<PermissionState | 'unsupported' | 'unknown'> {
    if (typeof navigator === 'undefined' || !navigator.permissions || !navigator.permissions.query) {
      this.permissionState.set('unknown');
      return 'unknown';
    }

    try {
      const status = await navigator.permissions.query({ name: 'geolocation' });
      this.permissionState.set(status.state);
      status.onchange = () => {
        this.permissionState.set(status.state);
      };
      return status.state;
    } catch (_) {
      this.permissionState.set('unknown');
      return 'unknown';
    }
  }

  /**
   * Prompts user for geolocation if LocationCheck is enabled and permission has not been decided yet.
   */
  public async promptIfRequired(): Promise<void> {
    const locSettings = this.settings.locationCheckSettings();
    if (!locSettings.Enabled) {
      return;
    }

    const isSecure = typeof window !== 'undefined' ? window.isSecureContext : true;
    const state = await this.checkPermissionState();



    if (!isSecure) {
      console.warn('[GeoService] Insecure context! Geolocation is disabled by the browser when not on HTTPS or localhost.');
      return;
    }

    // Always attempt to fetch position so that one-time permissions, session grants,
    // or native browser prompts are actually triggered and not blocked by stale query state
    try {
      await this.requestPosition();
    } catch (err) {
      console.warn('[GeoService] Position request failed or prompt was dismissed by user:', err);
    }
  }

  /**
   * Requests the current geolocation from the browser.
   * If not granted yet, browser will prompt the user.
   */
  public async requestPosition(forceFresh: boolean = false): Promise<UserCoordinates> {
    if (!forceFresh && this.userCoordinates()) {
      return this.userCoordinates()!;
    }

    this.isRequesting.set(true);
    this.lastError.set(null);

    try {
      const position = await getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: forceFresh ? 0 : 30000,
      });

      const coords: UserCoordinates = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };

      this.userCoordinates.set(coords);
      this.permissionState.set('granted');
      return coords;
    } catch (err: any) {
      console.warn('[GeoService] Geolocation request error details:', {
        code: err?.code,
        message: err?.message,
        PERMISSION_DENIED: err?.code === 1,
        POSITION_UNAVAILABLE: err?.code === 2,
        TIMEOUT: err?.code === 3,
      });
      if (err?.code === 1) {
        this.permissionState.set('denied');
      }
      const errorMsg = this.translate.instant('LOCATION.PERMISSION_REQUIRED') ||
        'Location verification is required to place an order. Please allow location permissions in your browser.';
      this.lastError.set(errorMsg);
      throw new Error(errorMsg);
    } finally {
      this.isRequesting.set(false);
    }
  }

  /**
   * Verifies diner location against venue coordinates.
   * Prompts for permission if not yet granted.
   * Throws localized error if permission denied or distance exceeded.
   */
  public async verifyLocation(): Promise<{ isWithin: boolean; distance: number; radius: number }> {
    const locSettings = this.settings.locationCheckSettings();
    if (!locSettings.Enabled) {
      return { isWithin: true, distance: 0, radius: 0 };
    }

    const venue = extractCoordinates(this.settings.coordinates());
    if (!venue) {
      return { isWithin: true, distance: 0, radius: 0 };
    }

    const radius = locSettings.RadiusInMeters || locSettings.radiusInMeters || 100;

    // Actively prompt and fetch position
    const userCoords = await this.requestPosition(true);

    const distance = calculateHaversineDistance(
      userCoords.latitude,
      userCoords.longitude,
      venue.latitude,
      venue.longitude
    );

    if (distance > radius) {
      const errorMsg = this.translate.instant('LOCATION.DISTANCE_EXCEEDED', {
        radius,
        distance,
      }) || `You must be within ${radius}m of the venue to place an order. (Your distance: ${distance}m)`;
      this.lastError.set(errorMsg);
      throw new Error(errorMsg);
    }

    return { isWithin: true, distance, radius };
  }
}
